import {
  LevelRewardInputType,
  SongpopLegacyChestAmountMode,
  SongpopLegacyChestPoolInput,
} from '../../../../../__gqltypes__';

/*
 * Editor shapes of the daily chest loot pool (sale config). Mirrors SP3BE
 * concerns/songpopLegacyPack/chestPool.ts; standalone, no dependency on the PRA screen.
 */

// One row. `_clientId` is an editor-only identity so React keys follow a row across removals.
export type ChestPoolItemRow = {
  _clientId: string;
  weight: number;
  rewardType: LevelRewardInputType;
  itemId: string | null;
  amountMode: SongpopLegacyChestAmountMode;
  amount: number; // EXACT
  minAmount: number; // RANGE
  maxAmount: number; // RANGE
};

export type ChestPoolDraft = {
  itemsPerOpening: number;
  items: ChestPoolItemRow[];
};

// Stored pool as returned by the API (SongpopLegacyChestPool).
type ChestPoolView = {
  itemsPerOpening: number;
  contents: Array<{
    weight: number;
    reward: { type: LevelRewardInputType; itemId?: string | null };
    amountMode: SongpopLegacyChestAmountMode;
    amount?: number | null;
    minAmount?: number | null;
    maxAmount?: number | null;
  }>;
};

// Same two exclusions as the Wheel of Fortune reward editor.
const FORBIDDEN_REWARD_TYPES = new Set<LevelRewardInputType>([
  LevelRewardInputType.CURRENCY_SPECIAL_EVENT,
  LevelRewardInputType.MONTHLY_BUFF,
]);

// Types without an item id: every other one names a boost, a playlist or an item.
const TYPES_WITHOUT_ITEM_ID = new Set<LevelRewardInputType>([
  LevelRewardInputType.CURRENCY_COIN,
  LevelRewardInputType.CURRENCY_DIAMOND,
  LevelRewardInputType.CURRENCY_TOURNAMENT_TICKET,
  LevelRewardInputType.CURRENCY_META_FRAGMENT_BRONZE,
  LevelRewardInputType.CURRENCY_META_FRAGMENT_SILVER,
  LevelRewardInputType.CURRENCY_META_FRAGMENT_GOLD,
  LevelRewardInputType.CURRENCY_SHARD_BRONZE,
  LevelRewardInputType.CURRENCY_SHARD_SILVER,
  LevelRewardInputType.CURRENCY_SHARD_GOLD,
  LevelRewardInputType.CURRENCY_ACCOLADE,
  LevelRewardInputType.CURRENCY_SUBSCRIPTION_DAY,
  LevelRewardInputType.CURRENCY_FREE_SPIN,
  LevelRewardInputType.CURRENCY_LOYALTY_POINT,
]);

let clientIdCounter = 0;
const nextClientId = (): string => `chest-item-${++clientIdCounter}`;

export function newChestItemRow(): ChestPoolItemRow {
  return {
    _clientId: nextClientId(),
    weight: 10,
    rewardType: LevelRewardInputType.CURRENCY_COIN,
    itemId: null,
    amountMode: SongpopLegacyChestAmountMode.EXACT,
    amount: 1,
    minAmount: 1,
    maxAmount: 1,
  };
}

export const newChestPool = (): ChestPoolDraft => ({ itemsPerOpening: 1, items: [newChestItemRow()] });

export function chestPoolViewToDraft(pool: ChestPoolView): ChestPoolDraft {
  return {
    itemsPerOpening: pool.itemsPerOpening,
    items: pool.contents.map((item) => ({
      _clientId: nextClientId(),
      weight: item.weight,
      rewardType: item.reward.type,
      itemId: item.reward.itemId ?? null,
      amountMode: item.amountMode,
      amount: item.amount ?? 1,
      minAmount: item.minAmount ?? 1,
      maxAmount: item.maxAmount ?? 1,
    })),
  };
}

export function chestPoolDraftToInput(pool: ChestPoolDraft): SongpopLegacyChestPoolInput {
  return {
    itemsPerOpening: pool.itemsPerOpening,
    contents: pool.items.map((item) => ({
      weight: item.weight,
      reward: { type: item.rewardType, itemId: item.itemId },
      amountMode: item.amountMode,
      // Only the active mode's fields, so the stored intent is unambiguous.
      ...(item.amountMode === SongpopLegacyChestAmountMode.EXACT
        ? { amount: item.amount }
        : { minAmount: item.minAmount, maxAmount: item.maxAmount }),
    })),
  };
}

export function formatChance(weight: number, totalWeight: number): string {
  if (!Number.isFinite(weight) || !Number.isFinite(totalWeight) || totalWeight <= 0) {
    return '—';
  }
  return `${((weight / totalWeight) * 100).toFixed(1)}%`;
}

// Same rules as the server (validateChestPool): shown under the table, Save stays off until fixed.
export function validateChestPoolDraft(pool: ChestPoolDraft, label: string): string[] {
  if (pool.items.length === 0) {
    return [`${label}: the pool needs at least one item`];
  }
  const errors: string[] = [];
  if (!Number.isInteger(pool.itemsPerOpening) || pool.itemsPerOpening < 1) {
    errors.push(`${label}: items per opening must be a whole number >= 1`);
  }
  pool.items.forEach((item, index) => {
    const itemLabel = `${label} item #${index + 1}`;
    if (!(item.weight > 0) || !Number.isFinite(item.weight)) {
      errors.push(`${itemLabel}: weight must be a positive number`);
    }
    if (item.amountMode === SongpopLegacyChestAmountMode.EXACT) {
      if (!Number.isInteger(item.amount) || item.amount < 1) {
        errors.push(`${itemLabel}: amount must be a whole number >= 1`);
      }
    } else {
      if (!Number.isInteger(item.minAmount) || item.minAmount < 1) {
        errors.push(`${itemLabel}: min must be a whole number >= 1`);
      }
      if (!Number.isInteger(item.maxAmount) || item.maxAmount < item.minAmount) {
        errors.push(`${itemLabel}: max must be a whole number >= min`);
      }
    }
    if (FORBIDDEN_REWARD_TYPES.has(item.rewardType)) {
      errors.push(`${itemLabel}: ${item.rewardType} cannot be in the chest`);
    } else if (!TYPES_WITHOUT_ITEM_ID.has(item.rewardType) && !item.itemId) {
      errors.push(`${itemLabel}: ${item.rewardType} needs its item`);
    }
  });
  return errors;
}
